/**
 * The roster for one activity (M1 track D5).
 *
 * Lives on the activity screen because that is where the question is asked — "who
 * is coming to this?" — and because D6 records attendance from the same page,
 * against the same list of names.
 *
 * Two things the API refuses, and this panel therefore explains rather than
 * hides:
 *   - a volunteer who is not ACTIVE cannot be assigned, so the picker searches
 *     active volunteers only;
 *   - a volunteer can only be assigned once, so anyone already on the roster is
 *     removed from the picker's results.
 *
 * Neither is enforcement — the API is (§8.40). They stop staff being offered a
 * choice that will be refused a second later.
 *
 * And one thing the API *asks about* rather than refuses (Plan D4): a volunteer
 * already committed elsewhere at that time. The picker cannot pre-empt that one —
 * whether two sessions clash depends on both their clocks, and a search result
 * has no way to know — so it arrives as a 409, is shown with the clashing
 * sessions named, and goes through on confirmation.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ASSIGNMENT_STATUS, VOLUNTEER_STATUS, personName } from '@parinaam-vms/shared';
import type { AssignmentDetail } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  ConfirmWarning,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  StatusBadge,
  SelectInput,
  TextInput,
} from '../../components/common';
import {
  useActivityAssignments,
  useCancelAssignment,
  useCreateAssignment,
} from '../../hooks/useAssignments';
import { useConfirmations } from '../../hooks/useConfirmations';
import { useVolunteers } from '../../hooks/useVolunteers';
import styles from './AssignmentPanel.module.css';
import { useActivitySessions } from '../../hooks/useSessions';
import { sessionLabel } from '../../services/api/sessions';

export interface AssignmentPanelProps {
  activityId: string;
  /**
   * The activity is closed to new assignments — cancelled or deleted. The API
   * refuses both (§4.22), so the panel does not offer the button.
   *
   * One flag rather than two: the panel's job is to know whether it may offer
   * an assignment, and it has no other reason to care which of the two states
   * the activity is in.
   */
  activityClosed: boolean;
  /** What to say when there is nobody on the roster and none can be added. */
  closedMessage?: string;
}

export function AssignmentPanel({
  activityId,
  activityClosed,
  closedMessage,
}: AssignmentPanelProps) {
  const [adding, setAdding] = useState(false);
  const [pendingCancel, setPendingCancel] = useState<string | null>(null);

  const roster = useActivityAssignments(activityId);
  const cancel = useCancelAssignment(activityId);

  const assignments = roster.data ?? [];
  const activeCount = assignments.filter(
    (row) => row.assignment_status !== ASSIGNMENT_STATUS.CANCELLED
  ).length;

  return (
    <Card
      title={`Assigned volunteers${activeCount > 0 ? ` (${activeCount})` : ''}`}
      action={
        !adding &&
        !activityClosed && (
          <Button variant="primary" onClick={() => setAdding(true)}>
            Assign a volunteer
          </Button>
        )
      }
    >
      {adding && (
        <VolunteerPicker
          activityId={activityId}
          assigned={assignments}
          onClose={() => setAdding(false)}
        />
      )}

      {cancel.isError && (
        <p className={styles.error} role="alert">
          {(cancel.error as Error).message}
        </p>
      )}

      {roster.isPending ? (
        <LoadingState />
      ) : roster.isError ? (
        <ErrorState error={roster.error} onRetry={() => void roster.refetch()} />
      ) : assignments.length === 0 ? (
        <EmptyState
          title="Nobody assigned yet"
          message={
            activityClosed
              ? (closedMessage ?? 'This activity has been cancelled.')
              : 'Assign volunteers so the roster is ready before the session.'
          }
        />
      ) : (
        <DataTable<AssignmentDetail>
          caption="Assigned volunteers"
          rows={assignments}
          getRowKey={(row) => row.assignment_id}
          columns={[
            {
              key: 'name',
              header: 'Volunteer',
              render: (row) => (
                <Link to={`/volunteers/${row.volunteer_id}`}>{row.volunteer_name}</Link>
              ),
            },
            { key: 'code', header: 'ID', render: (row) => row.volunteer_code },
            {
              key: 'session',
              header: 'Session',
              /*
                Which session the place is on — the question the roster is read
                to answer, now that participation hangs off a session rather than
                the activity (D19) and one activity can run several.

                This column used to be the note typed while assigning. That note
                is worth keeping and is still recorded, but it was blank on most
                rows and never told staff where the person was expected.

                It also used to print the phase name with the session name
                beneath it when the two differed. Phases were removed on
                13 Sep 2026 and there is one name now, so the second line went
                with them.
              */
              render: (row) => <span className={styles.session}>{row.session_name}</span>,
            },
            {
              key: 'status',
              header: 'Status',
              render: (row) => <StatusBadge status={row.assignment_status} />,
            },
            {
              key: 'action',
              header: '',
              render: (row) => {
                if (row.assignment_status === ASSIGNMENT_STATUS.CANCELLED) return null;

                // Cancelling is confirmed inline rather than in a dialog: the row
                // stays visible, so it is obvious which name is being removed.
                return pendingCancel === row.assignment_id ? (
                  <span className={styles.confirm}>
                    <Button
                      variant="danger"
                      loading={cancel.isPending}
                      onClick={() =>
                        cancel.mutate(row.assignment_id, {
                          onSuccess: () => setPendingCancel(null),
                        })
                      }
                    >
                      Confirm
                    </Button>
                    <Button onClick={() => setPendingCancel(null)}>Keep</Button>
                  </span>
                ) : (
                  <Button onClick={() => setPendingCancel(row.assignment_id)}>Cancel</Button>
                );
              },
            },
          ]}
        />
      )}
    </Card>
  );
}

/**
 * Search active volunteers and assign one.
 *
 * A search box rather than a dropdown: a `select` of every volunteer stops being
 * usable at a few hundred rows, and the list is server-filtered anyway.
 */
function VolunteerPicker({
  activityId,
  assigned,
  onClose,
}: {
  activityId: string;
  assigned: AssignmentDetail[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [notes, setNotes] = useState('');

  /**
   * Which session the place is on.
   *
   * Most activities have exactly one, and there a dropdown holding a single
   * option is noise — it asks a question with only one answer. So the picker
   * only appears once there is a genuine choice, and the single session is
   * selected silently.
   */
  const sessions = useActivitySessions(activityId);
  const [sessionId, setSessionId] = useState('');
  const available = sessions.data ?? [];
  const onlySession = available.length === 1 ? available[0]!.session_id : '';
  const chosenSession = sessionId || onlySession;
  const chosenSessionData = available.find((p) => p.session_id === chosenSession);
  // The API refuses this too (§4.22 spirit) — checked here so staff are told
  // before typing a search, the same reason already-assigned volunteers are
  // filtered from the results below rather than left for the API to reject.
  const sessionFull =
    chosenSessionData?.volunteers_needed != null &&
    chosenSessionData.assigned_count >= chosenSessionData.volunteers_needed;

  const create = useCreateAssignment(activityId);
  const confirmations = useConfirmations();

  /**
   * Who the warning is about. The results list keeps moving as staff type, so
   * "the volunteer I clicked Assign on" has to be held somewhere the next render
   * cannot lose — otherwise confirming a clash could assign the wrong person.
   */
  const [attempted, setAttempted] = useState<{ id: string; name: string } | null>(null);

  const assign = (volunteer: { id: string; name: string }, confirmed: boolean) => {
    if (!chosenSession) return;
    setAttempted(volunteer);
    create.mutate(
      {
        volunteer_id: volunteer.id,
        session_id: chosenSession,
        notes: notes.trim() || null,
        ...(confirmed ? confirmations.flags : {}),
      },
      {
        onSuccess: () => {
          setSearch('');
          setNotes('');
          setAttempted(null);
          confirmations.reset();
        },
        // Plan D4: a clash is a question, not a failure. Anything else falls
        // through to the error line below.
        onError: (error) => confirmations.remember(error),
      }
    );
  };

  const asking = confirmations.asked.length > 0;

  // Opening the picker lists active volunteers straight away (registration
  // observations): no term lists the first page of them, and typing narrows it.
  // Capped, because a directory of thousands is not a dropdown — past the cap the
  // hint below asks staff to keep typing.
  const term = search.trim();
  const RESULT_CAP = 50;
  const volunteers = useVolunteers({
    search: term || undefined,
    status: VOLUNTEER_STATUS.ACTIVE,
    page_size: RESULT_CAP,
  });

  const alreadyAssigned = new Set(
    assigned
      .filter((row) => row.assignment_status !== ASSIGNMENT_STATUS.CANCELLED)
      .map((row) => row.volunteer_id)
  );

  const results = (volunteers.data?.items ?? []).filter(
    (volunteer) => !alreadyAssigned.has(volunteer.volunteer_id)
  );

  // A full page means there are probably more active volunteers than the cap; the
  // hint tells staff to narrow rather than implying this is everyone.
  const moreThanShown = (volunteers.data?.items.length ?? 0) >= RESULT_CAP;

  return (
    <div className={styles.picker}>
      {create.isError && !asking && (
        <p className={styles.error} role="alert">
          {(create.error as Error).message}
        </p>
      )}

      <ConfirmWarning
        questions={confirmations.asked}
        confirmLabel={attempted ? `Assign ${attempted.name} anyway` : 'Assign anyway'}
        pending={create.isPending}
        onConfirm={() => attempted && assign(attempted, true)}
        onCancel={() => {
          confirmations.reset();
          setAttempted(null);
        }}
      />

      <div className={styles.pickerFields}>
        {available.length > 1 && (
          <SelectInput
            label="Session"
            required
            placeholder="Which session is this place on?"
            options={available.map((p) => ({
              value: p.session_id,
              label:
                p.volunteers_needed != null
                  ? `${sessionLabel(p)} — ${p.assigned_count}/${p.volunteers_needed} assigned`
                  : sessionLabel(p),
            }))}
            value={chosenSession}
            onChange={(event) => setSessionId(event.target.value)}
          />
        )}
        {chosenSessionData?.volunteers_needed != null && (
          <p className={sessionFull ? styles.error : styles.muted} role={sessionFull ? 'alert' : undefined}>
            {chosenSessionData.assigned_count}/{chosenSessionData.volunteers_needed} volunteers assigned
            {sessionFull ? ' — this session is full.' : '.'}
          </p>
        )}
        <TextInput
          label="Find a volunteer"
          placeholder="Name, code, email or city"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <TextInput
          label="Notes"
          placeholder="Optional, e.g. reading group A"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </div>

      {!asking && (
        <div className={styles.results}>
          {volunteers.isPending ? (
            <LoadingState label="Loading active volunteers..." />
          ) : volunteers.isError ? (
            /*
              A failed search is not an empty one, and the difference matters
              more here than almost anywhere else. "No volunteer matches" after a
              network failure tells staff the person is not in the system — so
              they add them again, and Parinaam has two records for one person.
              Say what actually happened and offer the retry.
            */
            <ErrorState
              error={volunteers.error}
              message="The search did not complete, so this is not a list of who is available."
              onRetry={() => void volunteers.refetch()}
            />
          ) : results.length === 0 ? (
            <p className={styles.noResults}>
              {term
                ? 'Nobody matches who is not already on.'
                : 'No active volunteers are available to add to this roster.'}
            </p>
          ) : (
            <ul className={styles.resultList}>
              {results.map((volunteer) => (
                <li key={volunteer.volunteer_id} className={styles.result}>
                  <span>
                    <strong>{personName(volunteer)}</strong>{' '}
                    <span className={styles.muted}>
                      {volunteer.volunteer_code}
                      {volunteer.city ? ` · ${volunteer.city}` : ''}
                    </span>
                  </span>
                  <Button
                    variant="primary"
                    disabled={sessionFull}
                    loading={create.isPending && attempted?.id === volunteer.volunteer_id}
                    onClick={() =>
                      assign({ id: volunteer.volunteer_id, name: personName(volunteer) }, false)
                    }
                  >
                    Assign
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {moreThanShown && !volunteers.isPending && !volunteers.isError && (
            <p className={styles.noResults}>
              Showing the first {RESULT_CAP}. Keep typing to find someone not listed here.
            </p>
          )}
        </div>
      )}

      <div className={styles.pickerActions}>
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}
